import React from 'react';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
import getChartColorsArray from '../../components/Common/ChartsDynamicColor';

const compact = (n) =>
  Number(n || 0).toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 1 });

/**
 * Combined snapshot: premium (KES, left axis, area) with claims and new
 * members (counts, right axis, columns) so the small series stay readable.
 */
const Spinearea = ({ dataColors, claims = [], premiums = [], members = [] }) => {
  const colors = getChartColorsArray(dataColors);

  // Align every series on one month list so points and columns line up even
  // when the three feeds cover slightly different ranges.
  // monthEnd is exclusive (1st of the following month), so step back a day to
  // land in the month the row actually covers.
  const monthKey = (d) => moment(d).subtract(1, 'day').format('YYYY-MM');
  const months = [...new Set([...premiums, ...claims, ...members].map((r) => monthKey(r?.monthEnd)))]
    .filter((k) => k && k !== 'Invalid date')
    .sort();
  const byMonth = (rows, field) => {
    const map = new Map(rows.map((r) => [monthKey(r?.monthEnd), Number(r?.[field]) || 0]));
    return months.map((k) => map.get(k) ?? 0);
  };
  const categories = months.map((k) => moment(k, 'YYYY-MM').format('MMM YYYY'));
  const premiumData = byMonth(premiums, 'totalPremiums');
  const memberData = byMonth(members, 'newMembers');
  const claimData = byMonth(claims, 'totalClaims');

  // one shared scale for both count series (a hidden axis would otherwise get its own)
  const counts = [...claimData, ...memberData];
  const rawMax = Math.max(1, ...counts.map((n) => Number(n) || 0));
  const step = 10 ** Math.floor(Math.log10(rawMax));
  const countMax = Math.ceil((rawMax * 1.1) / step) * step;

  const series = [
    { name: 'New Members', type: 'column', data: memberData },
    { name: 'Claims Reported', type: 'column', data: claimData },
    { name: 'Premium Paid', type: 'area', data: premiumData },
  ];

  const options = {
    chart: { type: 'line', stacked: false, toolbar: { show: false }, zoom: { enabled: false } },
    colors,
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [0, 0, 3] },
    markers: { size: [0, 0, 4], strokeWidth: 2, strokeColors: '#fff', hover: { size: 6 } },
    fill: {
      type: ['solid', 'solid', 'gradient'],
      opacity: [1, 1, 0.9],
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.4,
        opacityFrom: 0.3,
        opacityTo: 0.04,
        stops: [0, 100],
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '60%', // wider group so the two columns fill the slot
        borderRadius: 8,
        borderRadiusApplication: 'end', // round the top only, square at the baseline
      },
    },
    xaxis: {
      type: 'category',
      tickPlacement: 'on',
      categories,
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: [
      {
        seriesName: 'New Members',
        opposite: true,
        title: { text: 'New members / Claims', offsetX: -6, style: { fontWeight: 500, color: '#74788d' } },
        labels: { formatter: (v) => Math.round(v).toLocaleString(), minWidth: 44 },
        min: 0,
        max: countMax,
        tickAmount: 5,
      },
      {
        seriesName: 'Claims Reported',
        show: false, // same scale as new members
        min: 0,
        max: countMax,
        tickAmount: 5,
      },
      {
        seriesName: 'Premium Paid',
        title: { text: 'Premium (KES)', offsetX: 6, style: { fontWeight: 500, color: '#74788d' } },
        labels: { formatter: (v) => compact(v), offsetX: -6 },
        min: 0,
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (v, { seriesIndex }) =>
          seriesIndex === 2 ? `KES ${Number(v || 0).toLocaleString()}` : Number(v || 0).toLocaleString(),
      },
    },
    legend: { position: 'bottom', horizontalAlign: 'center', markers: { radius: 12 } },
    grid: { borderColor: '#f1f1f1', strokeDashArray: 3, padding: { left: 0, right: 12 } },
  };

  return <ReactApexChart options={options} series={series} type="line" height="380" width="100%" />;
};

export default Spinearea;
